/**
 * TypeScript interfaces for FileFormat entity JSON Schema fields
 *
 * These interfaces provide type-safe access to the `metadata_json`, `schema_json`,
 * `mapping_json`, and `validation_rules_json` JSONB columns in the `file_formats` table.
 *
 * **Architecture Note**: These TypeScript types are the source of truth for ALL definitions.
 * Const arrays define enum values (single source of truth), TypeScript types provide compile-time safety,
 * and Zod schemas in `file-format.schemas.ts` reference these const arrays for runtime validation.
 *
 * **Pattern**: Always define enum values as const arrays, derive types with `(typeof ARRAY)[number]`,
 * then import the const arrays into schemas for `z.enum()` validation.
 */
// Const arrays (single source of truth - imported by Zod schemas)
export const ENCODING_VALUES = ['UTF-8', 'UTF-16', 'ASCII', 'ISO-8859-1', 'Windows-1252'];
export const DELIMITER_VALUES = [',', '|', '\t', ';', '^'];
export const LINE_ENDING_VALUES = ['LF', 'CRLF', 'CR'];
export const PRIORITY_VALUES = ['LOW', 'NORMAL', 'HIGH', 'URGENT', 'CRITICAL'];
export const ARCHIVE_LOCATION_VALUES = ['s3', 'azure_blob', 'local', 'glacier'];
export const COMPRESSION_ALGORITHM_VALUES = ['gzip', 'zip', 'bzip2'];
export const ENCRYPTION_ALGORITHM_VALUES = ['aes_256', 'aes_128'];
export const DATA_CLASSIFICATION_VALUES = [
    'public',
    'internal',
    'confidential',
    'restricted',
];
export const NOTIFICATION_CHANNEL_VALUES = ['email', 'slack', 'teams', 'webhook', 'sms'];
export const ACCESS_CONTROL_LEVEL_VALUES = [
    'public',
    'internal',
    'confidential',
    'restricted',
];
export const SCHEMA_TYPE_VALUES = [
    'JSON_SCHEMA',
    'XML_SCHEMA',
    'CSV_SCHEMA',
    'FIXED_WIDTH',
    'CUSTOM',
];
export const FIELD_DATA_TYPE_VALUES = [
    'string',
    'integer',
    'decimal',
    'boolean',
    'date',
    'datetime',
    'time',
    'uuid',
    'email',
    'phone',
    'currency',
    'percentage',
    'url',
    'json',
    'array',
    'object',
    'binary',
];
export const TRANSFORMATION_TYPE_VALUES = [
    'DIRECT',
    'CAST',
    'TRIM',
    'UPPERCASE',
    'LOWERCASE',
    'CONCATENATE',
    'SUBSTRING',
    'REPLACE',
    'REGEX_REPLACE',
    'SPLIT',
    'DATE_FORMAT',
    'NUMBER_FORMAT',
    'LOOKUP',
    'CALCULATION',
    'CONDITIONAL',
    'CUSTOM_FUNCTION',
];
export const LOOKUP_TYPE_VALUES = ['static', 'database', 'external_api'];
export const CURRENCY_MAPPING_TYPE_VALUES = [
    'iso_to_symbol',
    'symbol_to_iso',
    'code_conversion',
];
export const COMPOSITE_MAPPING_TYPE_VALUES = [
    'CONCATENATE',
    'SPLIT',
    'AGGREGATE',
    'DERIVED',
];
export const APPROVAL_STATUS_VALUES = [
    'DRAFT',
    'PENDING_APPROVAL',
    'APPROVED',
    'DEPRECATED',
];
export const NULL_HANDLING_VALUES = ['reject', 'allow', 'convert_to_default'];
export const CHECKSUM_VALIDATION_VALUES = ['mod97', 'mod10', 'none'];
export const ROUNDING_MODE_VALUES = ['up', 'down', 'half_up', 'half_down', 'half_even'];
export const ERROR_SEVERITY_VALUES = ['info', 'warning', 'error', 'critical', 'fatal'];
export const REMEDIATION_ACTION_VALUES = [
    'reject_record',
    'quarantine_file',
    'notify_admin',
    'auto_correct',
    'manual_review',
    'skip_record',
    'halt_processing',
];
export const CONDITIONAL_OPERATOR_VALUES = [
    'equals',
    'not_equals',
    'greater_than',
    'less_than',
    'contains',
    'regex',
    'in_list',
];
export const CONDITIONAL_RULE_TYPE_VALUES = [
    'if_then',
    'if_then_else',
    'switch',
    'complex_expression',
];
export const CROSS_FIELD_VALIDATION_TYPE_VALUES = [
    'sum',
    'difference',
    'equality',
    'dependency',
    'mutual_exclusivity',
    'conditional_requirement',
];
export const DUPLICATE_SCOPE_VALUES = ['within_file', 'cross_file', 'database'];
export const DUPLICATE_ACTION_VALUES = ['reject', 'skip', 'merge', 'manual_review'];
export const REFERENCE_TYPE_VALUES = ['database_lookup', 'static_list', 'external_api'];
export const REFERENCE_ACTION_VALUES = ['reject', 'create', 'manual_review'];
export const INSTITUTION_LOOKUP_SOURCE_VALUES = ['federal_reserve', 'database', 'api'];
export const BALANCE_SOURCE_VALUES = ['database', 'api'];
export const VELOCITY_ACTION_VALUES = ['reject', 'manual_review', 'notify'];
export const THRESHOLD_ACTION_VALUES = [
    'manual_review',
    'notification',
    'approval_required',
];
export const SCREENING_PROVIDER_VALUES = ['internal', 'external_api'];
export const AML_ACTION_VALUES = ['reject', 'manual_review', 'escalate'];
export const SANCTIONS_ACTION_VALUES = ['reject', 'block', 'manual_review'];
// ==================== HELPER FUNCTIONS ====================
/**
 * Check if file format is high priority
 * @param metadata - File format metadata object
 * @returns True if priority is HIGH, URGENT, or CRITICAL
 */
export function isHighPriority(metadata) {
    const priority = metadata?.processing_priority;
    return priority === 'HIGH' || priority === 'URGENT' || priority === 'CRITICAL';
}
/**
 * Check if file format requires encryption
 * @param metadata - File format metadata object
 * @returns True if encryption is required
 */
export function requiresEncryption(metadata) {
    return metadata?.compliance_requirements?.encryption_required === true;
}
/**
 * Check if file format has compliance requirements
 * @param metadata - File format metadata object
 * @returns True if any compliance flags are enabled
 */
export function hasComplianceRequirements(metadata) {
    const compliance = metadata?.compliance_requirements;
    return !!(compliance?.pci_dss_required ||
        compliance?.sox_compliance ||
        compliance?.gdpr_compliance ||
        compliance?.hipaa_compliance);
}
/**
 * Get SLA deadline from current time
 * @param metadata - File format metadata object
 * @returns Deadline date object
 */
export function getSLADeadline(metadata) {
    const minutes = metadata?.sla_processing_time_minutes ?? 60;
    const deadline = new Date();
    deadline.setMinutes(deadline.getMinutes() + minutes);
    return deadline;
}
/**
 * Check if auto-quarantine is enabled
 * @param metadata - File format metadata object
 * @returns True if auto-quarantine is enabled
 */
export function isAutoQuarantineEnabled(metadata) {
    return metadata?.auto_quarantine_enabled === true;
}
/**
 * Get error threshold percentage
 * @param metadata - File format metadata object
 * @returns Error threshold percentage
 */
export function getErrorThreshold(metadata) {
    return metadata?.error_threshold_percent ?? 5;
}
/**
 * Get notification email recipients
 * @param metadata - File format metadata object
 * @returns Array of email addresses
 */
export function getNotificationRecipients(metadata) {
    return metadata?.notification_settings?.recipient_emails ?? [];
}
/**
 * Get escalation email addresses
 * @param metadata - File format metadata object
 * @returns Array of escalation email addresses
 */
export function getEscalationEmails(metadata) {
    return metadata?.notification_settings?.escalation_emails ?? [];
}
/**
 * Check if parallel processing is enabled
 * @param metadata - File format metadata object
 * @returns True if parallel processing is enabled
 */
export function isParallelProcessingEnabled(metadata) {
    return metadata?.workflow_integration?.parallel_processing === true;
}
/**
 * Get batch size for processing
 * @param metadata - File format metadata object
 * @returns Batch size
 */
export function getBatchSize(metadata) {
    return metadata?.workflow_integration?.batch_size ?? 1000;
}
/**
 * Get field definition by name
 * @param schema - File format schema object
 * @param fieldName - Field name to search for
 * @returns Field definition or undefined
 */
export function getFieldDefinition(schema, fieldName) {
    return schema?.field_definitions?.find((field) => field.field_name === fieldName);
}
/**
 * Get required field names
 * @param schema - File format schema object
 * @returns Array of required field names
 */
export function getRequiredFields(schema) {
    return (schema?.field_definitions?.filter((field) => field.required).map((f) => f.field_name) ?? []);
}
/**
 * Check if schema has security requirements
 * @param schema - File format schema object
 * @returns True if security requirements are defined
 */
export function hasSecurityRequirements(schema) {
    return schema?.security_requirements !== undefined;
}
/**
 * Get access control level
 * @param schema - File format schema object
 * @returns Access control level
 */
export function getAccessControlLevel(schema) {
    return schema?.security_requirements?.access_control_level ?? 'internal';
}
/**
 * Find field mapping by source field name
 * @param mapping - File format mapping object
 * @param sourceFieldName - Source field name to search for
 * @returns Field mapping or undefined
 */
export function getFieldMapping(mapping, sourceFieldName) {
    return mapping?.field_mappings?.find((fm) => fm.source_field.field_name === sourceFieldName);
}
/**
 * Get target table for source field
 * @param mapping - File format mapping object
 * @param sourceFieldName - Source field name
 * @returns Target table name or undefined
 */
export function getTargetTable(mapping, sourceFieldName) {
    return getFieldMapping(mapping, sourceFieldName)?.target_field.table_name;
}
/**
 * Get lookup table by name
 * @param mapping - File format mapping object
 * @param lookupName - Lookup name
 * @returns Lookup table or undefined
 */
export function getLookupTable(mapping, lookupName) {
    return mapping?.value_mappings?.lookup_tables?.find((lt) => lt.lookup_name === lookupName);
}
/**
 * Check if mapping is approved
 * @param mapping - File format mapping object
 * @returns True if mapping is approved
 */
export function isMappingApproved(mapping) {
    return mapping?.metadata?.approval_status === 'APPROVED';
}
/**
 * Get field validation for specific field
 * @param validationRules - File format validation rules object
 * @param fieldName - Field name
 * @returns Field validation or undefined
 */
export function getFieldValidation(validationRules, fieldName) {
    return validationRules?.field_validations?.[fieldName];
}
/**
 * Check if duplicate detection is enabled
 * @param validationRules - File format validation rules object
 * @returns True if duplicate detection is enabled
 */
export function isDuplicateDetectionEnabled(validationRules) {
    return validationRules?.business_rules?.duplicate_detection?.enabled === true;
}
/**
 * Get duplicate check fields
 * @param validationRules - File format validation rules object
 * @returns Array of field names used for duplicate detection
 */
export function getDuplicateCheckFields(validationRules) {
    return validationRules?.business_rules?.duplicate_detection?.duplicate_check_fields ?? [];
}
/**
 * Check if AML screening is enabled
 * @param validationRules - File format validation rules object
 * @returns True if AML screening is enabled
 */
export function isAMLScreeningEnabled(validationRules) {
    return validationRules?.business_rules?.compliance_rules?.aml_screening?.enabled === true;
}
/**
 * Get AML screening threshold
 * @param validationRules - File format validation rules object
 * @returns AML screening threshold amount
 */
export function getAMLThreshold(validationRules) {
    return (validationRules?.business_rules?.compliance_rules?.aml_screening?.screening_threshold ?? 10000);
}
/**
 * Get velocity checks for transaction validation
 * @param validationRules - File format validation rules object
 * @returns Array of velocity checks
 */
export function getVelocityChecks(validationRules) {
    return (validationRules?.business_rules?.financial_validations?.transaction_validations
        ?.velocity_checks ?? []);
}
/**
 * Get amount thresholds for transaction validation
 * @param validationRules - File format validation rules object
 * @returns Array of amount thresholds
 */
export function getAmountThresholds(validationRules) {
    return (validationRules?.business_rules?.financial_validations?.transaction_validations
        ?.amount_thresholds ?? []);
}
/**
 * Check if routing number validation is enabled
 * @param validationRules - File format validation rules object
 * @returns True if routing number checksum validation is enabled
 */
export function isRoutingNumberValidationEnabled(validationRules) {
    return (validationRules?.business_rules?.financial_validations?.account_validations
        ?.routing_number_validation?.validate_checksum === true);
}
//# sourceMappingURL=file-format.types.js.map